import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';

const TermsOfService = () => {
  return (
    <>
      <SEOHead
        title="Terms of Service - Namvoro"
        description="Namvoro's terms of service for enterprise digital brand management and cybersecurity solutions."
        keywords="terms of service, enterprise services, digital brand management, cybersecurity terms"
        canonicalUrl="https://namvoro.com/terms-of-service"
      />
      
      <div className="min-h-screen bg-white pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl font-bold text-slate-900 mb-8">Terms of Service</h1>
            <p className="text-slate-600 mb-8">Last updated: December 2024</p>
            
            <div className="prose prose-lg max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Acceptance of Terms</h2>
                <p className="text-slate-700 mb-4">
                  By accessing or using Namvoro's services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using our services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Description of Services</h2>
                <p className="text-slate-700 mb-4">
                  Namvoro provides enterprise-grade digital brand management services, including:
                </p>
                <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
                  <li>Domain acquisition and portfolio management</li>
                  <li>DNS and failover configuration</li>
                  <li>Email security and cybersecurity solutions</li>
                  <li>Brand protection and domain recovery</li>
                  <li>Infrastructure audits and compliance assessments</li>
                  <li>Strategic brand consulting</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. Account Registration</h2>
                <p className="text-slate-700 mb-4">
                  To access certain services, you must register for an account. You agree to:
                </p>
                <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Notify us immediately of any unauthorized use</li>
                  <li>Accept responsibility for all activities under your account</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Payment Terms</h2>
                <p className="text-slate-700 mb-4">
                  Payment terms are specified in your service agreement. Generally:
                </p>
                <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
                  <li>Fees are due in advance of service delivery</li>
                  <li>Late payments may result in service suspension</li>
                  <li>Refunds are subject to our refund policy</li>
                  <li>Price changes will be communicated 30 days in advance</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Service Level Agreement</h2>
                <p className="text-slate-700 mb-4">
                  We strive to maintain 99.9% uptime for our services. Our SLA includes:
                </p>
                <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
                  <li>24/7 monitoring and support</li>
                  <li>Rapid response to service issues</li>
                  <li>Proactive maintenance and updates</li>
                  <li>Service credits for significant downtime</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Acceptable Use</h2>
                <p className="text-slate-700 mb-4">You agree not to use our services for:</p>
                <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
                  <li>Illegal activities or violation of laws</li>
                  <li>Distributing malware or conducting cyberattacks</li>
                  <li>Spamming or unsolicited communications</li>
                  <li>Infringing on intellectual property rights</li>
                  <li>Activities that could harm our infrastructure</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Intellectual Property</h2>
                <p className="text-slate-700 mb-4">
                  Our services, software, and content are protected by intellectual property laws. You retain rights to your data, while we retain rights to our proprietary technologies and methods.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Limitation of Liability</h2>
                <p className="text-slate-700 mb-4">
                  To the maximum extent permitted by law, Namvoro shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Termination</h2>
                <p className="text-slate-700 mb-4">
                  Either party may terminate services with appropriate notice as specified in your service agreement. Upon termination, you remain responsible for any outstanding fees.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">10. Governing Law</h2>
                <p className="text-slate-700 mb-4">
                  These terms are governed by the laws of New York State, without regard to conflict of law principles.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">11. Contact Information</h2>
                <p className="text-slate-700 mb-4">
                  For questions about these Terms of Service, please contact us at:
                </p>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-slate-700">
                    <strong>Email:</strong> legal@namvoro.com<br />
                    <strong>Phone:</strong> +1 855-425-5551<br />
                    <strong>Address:</strong> 200 Broadway, Floor 3, New York, NY 10038
                  </p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;